<template>
  <div :class="type" class="payment">
    <AppHeader />
    <Banner />
    <div class="user-paymethod">
      <div class="blue-line-1"></div>
      <div class="blue-line-2"></div>
      <div class="blue-line-3"></div>
      <ValidateUser
        v-model:cpUserId="state.cpUserId"
        :validatedUser="state.validatedUser"
        :cpUserInfo="state.cpUserInfo"
        @reset="resetUserInfo"
        @validate="getCpUserInfo"
      />
      <PaymentMethod
        v-model="state.paymentMethodId"
        :payment-methods="state.paymentMethods"
        :isShowProduct="state.isShowProduct"
        @change="handlePaymentMethod"
        @confirm="goPay"
      />
    </div>
    <Product
      class="product"
      v-if="state.isShowProduct"
      v-model="state.productCode"
      :products="state.products"
      @confirm="goPay"
    />
    <AppFooter :class="{ 'no-product-footer': !state.isShowProduct }" />
    <AppFloat :userInfo="state.cpUserInfo" />
    <Dialog
      v-model="state.validateDialog.visible"
      :confirmText="state.validateDialog.confirmText"
      :cancelText="state.validateDialog.cancelText"
      @confirm="state.validateDialog.onConfirm"
      @cancel="state.validateDialog.onCancel"
    >
      <div class="validate-dialog-content">
        <div class="userinfo">
          <div class="item id">
            <span class="label">{{ $t('payment.userDialog.id') }}:</span>
            <span class="value">{{ state.cpUserInfo.cpUserId }}</span>
          </div>
          <div class="item name">
            <span class="label">{{ $t('payment.userDialog.name') }}:</span>
            <span class="value">{{ state.cpUserInfo.cpNickName }}</span>
          </div>
          <div class="confirm-text">
            {{ $t('payment.userDialog.confirmText') }}
          </div>
        </div>
      </div>
    </Dialog>
    <Dialog
      v-model="state.payStatusDialog.visible"
      :cancelText="state.payStatusDialog.cancelText"
      @cancel="state.payStatusDialog.onCancel"
    >
      <div class="paystatus-dialog-content">
        <div class="paystatus-dialog-title">
          {{ state.payStatus === 0 ? t('payment.payStatusDialog.paying') : state.payStatus === 1 ? t('payment.payStatusDialog.paySuccess') : t('payment.payStatusDialog.payFailed') }}
        </div>
        <div class="paystatus-dialog-icon" v-if="state.payStatus === 0"></div>
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { reactive, computed, ref, watch } from 'vue'
import { isMobile, isIOS} from '@/utils'
import AppHeader from '@/components/AppHeader'
import AppFooter from '@/components/AppFooter'
import AppFloat from '@/components/AppFloat'
import Banner from './components/Banner'
import PaymentMethod from './components/PaymentMethod'
import ValidateUser from './components/ValidateUser'
import Product from './components/Product'
import Dialog from './components/Dialog'
import Api from '@/config/api'
import { useI18n } from 'vue-i18n'
import { toastService } from '@/utils/toastService'
import { useLog } from '@/composables/useLog'
import { useStore } from 'vuex'
const store = useStore()
const locale = computed(() => store.state.locale)
watch(locale, () => {
  reset()
})
const log = useLog()
log.track('payment_page_entry', {
  referrer: document.referrer
})
const { t } = useI18n()
const type = isMobile ? 'mobile' : 'pc'
const state = reactive({
  cpUserInfo: computed(() => store.state.cpUserInfo),
  cpUserId: '',
  validatedUser: store.state.cpUserInfo.cpUserId ? true : false,
  validateDialog: {
    visible: false,
    confirmText: computed(() => t('payment.userDialog.confirmInput')),
    cancelText: computed(() => t('payment.userDialog.cancelInput')),
    onConfirm: () => {
      state.validatedUser = true
      resetData()
      log.track('user_verification_confirm', {})
    },
    onCancel: () => {
      log.track('user_verification_cancel', {})
      state.validatedUser = false
      state.cpUserId = ''
      store.commit('SET_CP_USER_INFO', {})
    },
  },
  paymentMethods: [],
  paymentMethodId: null,
  paymentMethod: computed(() => state.paymentMethods.find(method => method.id === state.paymentMethodId)),
  isShowProduct: computed(() => {
    if (state.paymentMethod?.type !== 2) { // type=2是点卡
      return true
    }
    const MY_CARD_DIANKA = ['9'] // myCard实体卡（点卡），需要展示商品，其他的点卡不展示商品
    return MY_CARD_DIANKA.includes(state.paymentMethod.channelId)
  }),
  products: [],
  product: computed(() => state.products.find(product => product.productCode === state.productCode)),
  productCode: null,
  mergeOrderId: null,
  payStatus: 0, // 0: 未支付, 1: 已支付, 2: 支付失败
  getPayStatusInterval: null,
  payStatusDialog: {
    visible: false,
    cancelText: computed(() => t('payment.payStatusDialog.cancelText')),
    onCancel: () => {
      console.log('onCancel')
      state.payStatus = 0
      clearInterval(state.getPayStatusInterval)
    },
  },
  queryTimestamps: [], // 添加查询时间戳数组
})

if (state.cpUserInfo.cpUserId) {
  state.validatedUser = true
}

const handlePaymentMethod = (method) => {
  // log.track('payment_method_change', {
  //   payment_method_id: method.id,
  // })
  state.products = method.products || []
  if (state.products.length > 0) {
    state.productCode = state.products[0].productCode
    // log.track('product_change', {
    //   product_id: state.products[0].productCode,
    // })
  } else {
    state.productCode = null
  }
}

// const handleProductChange = (productCode) => {
//   const product = state.products.find(product => product.productCode === productCode)
//   log.track('product_change', {
//     product_id: product.productCode,
//   })
// }

const g_gift_coin = ref(0)
const goPay = async () => {
  if (!state.validatedUser) {
    toastService.show(t('payment.validateUser.noUserText'))
    return
  }
  const params = {
    cpUserId: state.cpUserInfo.cpUserId,
    accountNum: state.cpUserInfo.accountNum,
    payWayId: state.paymentMethodId,
    platformId: state.paymentMethod.platformId,
    channelId: state.paymentMethod.channelId,
    productCode: state.productCode,
    productName: state.product?.productName,
    discountPrice: state.product?.discountPrice,
    productCount: state.product ? state.product?.coin + state.product?.giftCoin : 0,
    price: state.product?.price,
    orderDesc: 'pay',
    extension: JSON.stringify({
      frontCallbackUrl: `${window.location.origin}${process.env.VUE_APP_HISTORY_URL}${locale.value}/callback/common`,
    })
  }
  params.deviceType = isMobile ? "2" : "1"
  console.log('product', state.product)
  console.log('params', params)
  try {
    const res = await Api.createWebOrder(params)
    /* 测试!!!!!! start */
    // if (process.env.VUE_APP_ENV !== 'prod') {
    //   const url = `https://ovs-agg-test.miniworldplus.com/pay/cs/payCallback?cpOrderId=${res.mergeOrderId}&signature=ckowuu0sq8ha&orderId=testsd112`
    //   window.open(url, '_blank')
    // }
   /* 测试!!!!!! end */
    console.log('createWebOrder res', res)
    g_gift_coin.value = state.isShowProduct ? state.product?.giftCoin : 0
  
    log.track('confirm_payment', {
      order_id: res.mergeOrderId,
      payment_method_id: state.paymentMethodId,
      product_id: state.productCode,
      payment_amount: state.product?.price,
      currency: state.product?.currency,
      coin: state.product?.coin,
      gift_coin: g_gift_coin.value,
    })
    if (res.payUrl) {
      log.track('third_party_redirect', {
        order_id: res.mergeOrderId,
        payment_method_id: state.paymentMethodId,
        product_id: state.productCode,
        payment_amount: state.product?.price,
        currency: state.product?.currency,
        coin: state.product?.coin,
        gift_coin: g_gift_coin.value
      })
      try {
        res.payUrl = decodeURIComponent(res.payUrl)
      } catch (error) {
        console.error('decodeURIComponent error', error)
      }
      console.log('res.payUrl', res.payUrl)
      state.mergeOrderId = res.mergeOrderId
      state.payStatus = 0
      state.getPayStatusInterval = setInterval(() => {
        getPayStatus()
      }, 5000)
      state.payStatusDialog.visible = true
      if (isMobile) {
        setTimeout(() => {
          if (isIOS) {
            window.location.href = res.payUrl // ios不能使用window.open，部分苹果浏览器会拦截
          } else {
            window.open(res.payUrl, '_blank')
          }
        }, 10)
      } else {
        setTimeout(() => {
          window.open(res.payUrl, '_blank')
        }, 10)
      }
      if (isMobile && isIOS) {
        setTimeout(() => { // ios中如果前面使用location.href跳转，同时发请求或收到axios请求会被取消，报一个网络错误，所以这里延迟3秒再重置数据
          resetData()
        }, 3000)
      } else {
        resetData()
      }
    }
  } catch (error) {
    console.error('goPay error', error)
  }
}

const getPayStatus = async () => {
  const params = {
    mergeOrderId: state.mergeOrderId,
  }
  const res = await Api.queryWebOrderById(params)
  console.log('getPayStatus res', res)
  if (res.orderState === 20) {
    log.track('payment_success', {
      order_id: state.mergeOrderId,
      payment_method_id: state.paymentMethodId,
      product_id: state.productCode,
      payment_amount: res.amount,
      currency: state.product?.currency,
      coin: res.productCount - g_gift_coin.value,
      gift_coin: g_gift_coin.value,
    })
    state.payStatus = 1
    clearInterval(state.getPayStatusInterval)
    resetData()
  } else if (res.orderState === 30) {
    log.track('payment_failed', {
      order_id: state.mergeOrderId,
      payment_method_id: state.paymentMethodId,
      product_id: state.productCode,
      payment_amount: res.amount,
      currency: state.product?.currency,
      coin: state.product?.coin,
      gift_coin: g_gift_coin.value,
    })
    state.payStatus = 2
    clearInterval(state.getPayStatusInterval)
    resetData()
  }
}

const checkQueryTime = () => {
  const now = Date.now()
  const oneMinuteAgo = now - 60 * 1000
  
  // 清除一分钟前的记录
  state.queryTimestamps = state.queryTimestamps.filter(timestamp => timestamp > oneMinuteAgo)
  
  // 检查最近1分钟内的查询次数
  if (state.queryTimestamps.length >= 5) {
    // 查询次数超过限制
    
    toastService.show(t('payment.validateUser.queryLimitExceeded'))
    return false
  }
  
  // 记录本次查询时间戳
  state.queryTimestamps.push(now)
  return true
}

const getCpUserInfo = async () => {
  const isPassed = checkQueryTime()
  if (!isPassed) {
    return
  }
  log.track('user_verification_click', {
    verification_id: state.cpUserId
  })
  const params = {
    cpUserId: state.cpUserId,
  }
  try {
    const res = await Api.getCpUserInfo(params)
    console.log('getCpUserInfo res', res)
    if (res.cpUserId) {
      store.commit('SET_CP_USER_INFO', res)
      state.validateDialog.visible = true
    }
  } catch(error) {
    console.log('getCpUserInfo error', error)
    if (error.tars_ret === 3001) {
      console.log('error.tars_ret === 3001')
      toastService.show(t('payment.validateUser.userNotFound'))
    }
  }
}

const resetUserInfo = () => {
  store.commit('SET_CP_USER_INFO', {})
  state.cpUserId = ''
  state.validatedUser = false
  resetData()
}

const reset = () => {
  state.paymentMethods = []
  state.products = []
  state.productCode = null
  init()
}
const resetData = () => {
  init()
}
// 页面初始化
const init = async () => {
  const productParams = {}
  if (state.cpUserInfo?.accountNum) {
    productParams.accountNum = state.cpUserInfo.accountNum
  }
  const productRes = await Api.getProductList(productParams)
  const payMethodRes = await Api.getPayMethodList()
  const productList = productRes.list || []
  const payMethods = payMethodRes.list || []
  
  // 处理支付方式数据
  state.paymentMethods = payMethods.map(method => {
    // 基础支付方式信息
    const paymentMethod = {
      id: method.payWayId,
      payWayId: method.payWayId,
      name: method.payWayName,
      icon: method.iconUrl,
      type: method.payType,
      position: method.position,
      recommendFlag: method.recommendFlag,
      platformId: method.platformId,
      channelId: method.channelId,
    }
    const relatedProducts = productList.filter(product => 
      product.payWayIds.includes(method.payWayId)
    ).sort((a, b) => a.position - b.position)
    paymentMethod.products = relatedProducts
    
    return paymentMethod
  }).sort((a, b) => a.position - b.position)
  if (state.paymentMethods.length > 0) {
    console.log('111state.paymentMethods', state.paymentMethodId, JSON.parse(JSON.stringify(state.paymentMethods)))
    if (!state.paymentMethodId || !state.paymentMethods.find(method => method.id === state.paymentMethodId)) {
      state.paymentMethodId = state.paymentMethods[0].id
    }
    const paymentMethod = state.paymentMethods.find(method => method.id === state.paymentMethodId)
    state.products = paymentMethod.products || []
    // log.track('payment_method_change', {
    //   payment_method_id: state.paymentMethods[0].id,
    // })
    if (state.products.length > 0) {
      if (!state.productCode || !state.products.find(product => product.productCode === state.productCode)) {
        state.productCode = state.products[0].productCode
      }
      // log.track('product_change', {
      //   product_id: state.products[0].productCode,
      // })
    }
  }
  console.log('state.paymentMethods', state.paymentMethods)
  console.log('state.products', state.products)
  console.log('state.productCode', state.productCode)
}

init()


</script>

<style src="./pc_style.less" lang="less" scoped></style>
<style src="./mobile_style.less" lang="less" scoped></style>
